import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useApi, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { fmt, mondayOf, toISO, weekDays } from "@/lib/dates";
import { PageHeader, Panel, Loader, Avatar, Empty } from "@/components/common";
import { WeekPicker } from "@/components/WeekPicker";
import { ShiftDialog } from "@/components/ShiftDialog";
import { CalendarDays } from "lucide-react";
import { AiScheduleDialog } from "@/components/AiScheduleDialog";
import { dayStatus } from "@/lib/availability";
import { SwapOfferDialog } from "@/components/SwapOfferDialog";
import { ScheduleMailBar } from "@/components/ScheduleMailBar";

const ShiftChip = ({ s, onClick, mine }) => (
  <button
    onClick={onClick}
    className={`w-full text-left rounded-lg px-2 py-1.5 text-xs transition-colors ${mine ? "bg-[#FFECE8] text-[#9A2B12] hover:bg-[#FFDCD3]" : "bg-[#E6F0EC] text-[#0D5C46] hover:bg-[#D3E6DE]"}`}
    data-testid={`shift-chip-${s.id}`}
  >
    <span className="font-mono-num font-semibold">{s.start}–{s.end}</span>
    {s.position && <span className="block truncate opacity-80">{s.position}</span>}
  </button>
);

export default function Planning() {
  const api = useApi();
  const { member, isManager } = useMember();
  const [monday, setMonday] = useState(mondayOf());
  const [shifts, setShifts] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [onlyMine, setOnlyMine] = useState(false);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [avail, setAvail] = useState({});
  const [offer, setOffer] = useState(null);
  const days = useMemo(() => weekDays(monday), [monday]);

  const load = useCallback(async () => {
    const [s, e] = await Promise.all([api.get("/shifts", { params: { week_start: toISO(monday) } }), api.get("/employees")]);
    setShifts(s.data);
    setEmployees(e.data.filter((x) => x.active));
    if (isManager) {
      const a = await api.get("/availability");
      setAvail(Object.fromEntries(a.data.map((x) => [x.user_id, x])));
    }
  }, [api, monday, isManager]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (initial) => {
    if (isManager) setDialog({ open: true, initial });
    else if (initial?.id && initial.user_id === member.user_id) setOffer(initial);
  };
  const rows = onlyMine ? employees.filter((e) => e.user_id === member.user_id) : employees;
  const cell = (uid, d) => (shifts || []).filter((s) => s.user_id === uid && s.date === toISO(d));

  const copyWeek = async () => {
    try {
      const { data } = await api.post("/shifts/copy-week", null, { params: { week_start: toISO(monday) } });
      toast.success(`${data.copied} diensten gekopieerd van vorige week`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="planning-page">
      <PageHeader title="Weekplanning" subtitle={isManager ? "Klik op een vakje om een dienst in te plannen." : "Kun je niet? Tik op je eigen dienst om hem aan te bieden."}>
        {isManager && (
          <>
            <AiScheduleDialog weekStart={toISO(monday)} onSaved={load} />
            <Button variant="outline" className="rounded-full" onClick={copyWeek} data-testid="copy-week-button"><Copy className="h-4 w-4 mr-2" />Kopieer vorige week</Button>
            <Button className="rounded-full" onClick={() => open({ date: toISO(days[0]) })} data-testid="add-shift-button"><Plus className="h-4 w-4 mr-2" />Dienst toevoegen</Button>
          </>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <WeekPicker monday={monday} onChange={setMonday} />
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Switch checked={onlyMine} onCheckedChange={setOnlyMine} data-testid="only-mine-switch" /> Alleen mijn diensten
        </label>
      </div>

      {isManager && <ScheduleMailBar weekStart={toISO(monday)} />}

      {!shifts ? <Loader /> : rows.length === 0 ? (
        <Empty icon={CalendarDays} title="Nog geen medewerkers" text={isManager ? "Voeg eerst medewerkers toe via Medewerkers." : "Er zijn nog geen collega's gekoppeld."} />
      ) : (
        <>
          <Panel className="hidden md:block p-0 overflow-x-auto" data-testid="week-schedule-grid">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-3 w-48 text-xs font-semibold uppercase tracking-wider text-slate-500">Medewerker</th>
                  {days.map((d) => (
                    <th key={toISO(d)} className={`p-3 text-left text-xs font-semibold uppercase tracking-wider ${toISO(d) === toISO(new Date()) ? "text-[#FF5A36]" : "text-slate-500"}`}>
                      {fmt(d, "EEE d")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.user_id} className="border-b border-slate-100 last:border-0 align-top" data-testid={`schedule-row-${e.user_id}`}>
                    <td className="p-3"><div className="flex items-center gap-2"><Avatar name={e.name} size="h-8 w-8" /><span className="font-medium truncate">{e.name}</span></div></td>
                    {days.map((d) => {
                      const st = dayStatus(avail[e.user_id], toISO(d));
                      return (
                      <td key={toISO(d)} className={`p-1.5 ${st?.off ? "bg-slate-50" : ""}`}>
                        <div className="space-y-1 min-h-[44px]">
                          {st && <p className={`text-[10px] font-semibold ${st.off ? "text-slate-600" : "text-emerald-700"}`} data-testid={`avail-hint-${e.user_id}-${toISO(d)}`}>{st.text}</p>}
                          {cell(e.user_id, d).map((s) => <ShiftChip key={s.id} s={s} mine={s.user_id === member.user_id} onClick={() => open(s)} />)}
                          {isManager && (
                            <button onClick={() => open({ user_id: e.user_id, date: toISO(d) })} className="w-full rounded-lg border border-dashed border-transparent hover:border-slate-300 text-slate-400 hover:text-slate-600 py-1 grid place-items-center transition-colors" data-testid={`add-shift-cell-${e.user_id}-${toISO(d)}`} aria-label="Dienst toevoegen">
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <div className="md:hidden space-y-3" data-testid="week-schedule-list">
            {days.map((d) => {
              const list = (shifts || []).filter((s) => s.date === toISO(d) && rows.some((r) => r.user_id === s.user_id));
              return (
                <Panel key={toISO(d)} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold capitalize">{fmt(d, "EEEE d MMM")}</p>
                    {isManager && <Button size="sm" variant="ghost" onClick={() => open({ date: toISO(d) })} data-testid={`mobile-add-shift-${toISO(d)}`}><Plus className="h-4 w-4" /></Button>}
                  </div>
                  {isManager && (() => {
                    const offs = rows.map((r) => [r, dayStatus(avail[r.user_id], toISO(d))]).filter(([, st]) => st);
                    return offs.length > 0 && (
                      <p className="text-xs text-slate-600 mb-1" data-testid={`mobile-avail-hints-${toISO(d)}`}>
                        {offs.map(([r, st]) => `${r.name.split(" ")[0]}: ${st.text}`).join(" · ")}
                      </p>
                    );
                  })()}
                  {list.length === 0 ? <p className="text-sm text-slate-400">Geen diensten</p> : list.map((s) => (
                    <button key={s.id} onClick={() => open(s)} className="w-full flex items-center gap-3 py-2 text-left" data-testid={`mobile-shift-${s.id}`}>
                      <Avatar name={employees.find((e) => e.user_id === s.user_id)?.name} size="h-8 w-8" />
                      <span className="flex-1 text-sm font-medium">{employees.find((e) => e.user_id === s.user_id)?.name}<span className="block text-xs text-slate-500">{s.position}</span></span>
                      <span className="font-mono-num text-sm font-semibold">{s.start}–{s.end}</span>
                    </button>
                  ))}
                </Panel>
              );
            })}
          </div>
        </>
      )}

      <SwapOfferDialog shift={offer} employees={employees} onClose={() => setOffer(null)} onSaved={load} />
      <ShiftDialog open={dialog.open} onOpenChange={(o) => setDialog((p) => ({ ...p, open: o }))} initial={dialog.initial} employees={employees} availability={avail} onSaved={load} />
    </div>
  );
}
